import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { caseHasDiscoveryEvidence } from './PathUtils.js';

const MEASUREMENT_ID = 'G-3D7S5LTGQ2';

function isEnabled() {
    return process.env.NODE_ENV === 'production'
        && process.env.REACT_APP_GA_ENABLED !== 'false';
}

function isDevelopment() {
    return process.env.NODE_ENV === 'development';
}

function logAnalytics(eventName, params = {}) {
    if (isDevelopment()) {
        console.log('[analytics]', eventName, params);
    }
}

let initialized = false;

/**
 * Normalize paths for GA aggregation (UUIDs and slugs → route templates).
 */
export function sanitizePath(pathname) {
    return pathname
        .replace(/\/case\/[^/]+/, '/case/:caseId')
        .replace(/\/press\/[^/]+/, '/press/:slug');
}

export function getCaseTabSlugs(caseInfo) {
    const showDiscovery = caseHasDiscoveryEvidence(caseInfo);
    const showIncidentDetails = (caseInfo?.documents
        && Object.keys(caseInfo.documents).some(
            (filename) => caseInfo.documents[filename].analysis?.incident_details?.length > 0
        ))
        || (caseInfo?.analysis?.incident_summary?.incident_date
            && !Number.isNaN(new Date(caseInfo.analysis.incident_summary.incident_date).getTime()));

    const tabs = ['documents'];
    if (showIncidentDetails) {
        tabs.push('incident_details');
    }
    tabs.push('medical_chronology', 'medical_expenses', 'timeline');
    if (showDiscovery) {
        tabs.push('discovery');
    }
    tabs.push('drafting');
    return tabs;
}

export function initAnalytics() {
    if (!isEnabled() || initialized) {
        return;
    }
    ReactGA.initialize(MEASUREMENT_ID);
    initialized = true;
}

export function trackPageView(pathname) {
    const page = sanitizePath(pathname);
    logAnalytics('pageview', { page, title: page });
    if (!isEnabled()) {
        return;
    }
    ReactGA.send({ hitType: 'pageview', page, title: page });
}

export function trackEvent(eventName, params = {}) {
    logAnalytics(eventName, params);
    if (!isEnabled()) {
        return;
    }
    ReactGA.event(eventName, params);
}

export function trackLogin(method) {
    trackEvent('login', { method });
}

export function trackSignUp(method) {
    trackEvent('sign_up', { method });
}

export function trackCreateCase() {
    trackEvent('create_case');
}

export function trackUploadCaseDocuments(fileCount) {
    trackEvent('upload_case_documents', {
        file_count: fileCount,
    });
}

export function trackViewTab(tabName) {
    trackEvent('view_tab', { tab_name: tabName });
}

export function trackCopyContent(contentType) {
    trackEvent('copy_content', { content_type: contentType });
}

export function trackDocumentChoicesPanelClick(action) {
    trackEvent('document_choices_panel_click', { action });
}

export function trackDocumentToggle({ category, included }) {
    trackEvent('document_toggle_click', {
        category,
        included,
    });
}

/**
 * Final path segment from a JSONPath textPath (e.g. "...patient_name" → "patient_name").
 */
export function getFieldNameFromTextPath(textPath) {
    if (!textPath || typeof textPath !== 'string') {
        return 'unknown';
    }

    const dotSegment = textPath.match(/\.([a-zA-Z_][a-zA-Z0-9_]*)$/);
    if (dotSegment) {
        return dotSegment[1];
    }

    const quotedKey = textPath.match(/\['([^']+)'\]$|\["([^"]+)"\]$/);
    if (quotedKey) {
        return quotedKey[1] ?? quotedKey[2];
    }

    const indexSegment = textPath.match(/\[(\d+)\]$/);
    if (indexSegment) {
        return indexSegment[1];
    }

    return 'unknown';
}

export function trackEditField(fieldName) {
    trackEvent('edit_field', { field_name: fieldName });
}

export function trackWebVital({ name, delta, id, value }) {
    trackEvent('web_vitals', {
        metric_name: name,
        metric_id: id,
        metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
        metric_delta: Math.round(name === 'CLS' ? delta * 1000 : delta),
    });
}

/**
 * Sends a sanitized page_view on each SPA route change.
 */
export function AnalyticsTracker() {
    const { pathname } = useLocation();

    useEffect(() => {
        trackPageView(pathname);
    }, [pathname]);

    return null;
}
